import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { GoldButton } from "@/components/Brand";
import AdminPrintPrices from "@/components/admin/AdminPrintPrices";

const PRODUCT_TYPES = [
  "digital_download", "high_res_digital", "print_4x6", "print_5x7", "print_8x10",
  "custom_print", "event_package", "gallery_package",
];
const field = "min-h-[44px] w-full border border-[#C69A3B]/60 bg-white px-3 text-sm";

export default function AdminPricing() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    product_type: PRODUCT_TYPES[0], package_name_es: "", package_name_en: "",
    price: "", kind: "digital", download_rights: "personal", active: false,
  });

  const load = useCallback(() => {
    api.get("/pricing").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const save = async () => {
    try {
      await api.post("/admin/pricing", { ...form, price: parseFloat(form.price || "0") });
      setForm((f) => ({ ...f, package_name_es: "", package_name_en: "", price: "" }));
      load();
      toast.success(t("btn.save"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-pricing-panel">
      <p className="border border-dashed border-[#C69A3B] p-4 text-sm text-[#5A5752]">
        {t("services.pricingSoon")} — la estructura de productos y pedidos ya existe; el checkout se activará cuando lo autorice.
      </p>

      <div className="mt-6 grid max-w-3xl gap-3 sm:grid-cols-2">
        <select className={field} data-testid="pricing-type" value={form.product_type} onChange={(e) => setForm((f) => ({ ...f, product_type: e.target.value }))}>
          {PRODUCT_TYPES.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select className={field} data-testid="pricing-kind" value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}>
          <option value="digital">digital</option>
          <option value="print">print</option>
        </select>
        <input className={field} placeholder="Paquete — Español" data-testid="pricing-name-es" value={form.package_name_es} onChange={(e) => setForm((f) => ({ ...f, package_name_es: e.target.value }))} />
        <input className={field} placeholder="Package — English" data-testid="pricing-name-en" value={form.package_name_en} onChange={(e) => setForm((f) => ({ ...f, package_name_en: e.target.value }))} />
        <input className={field} type="number" step="0.01" placeholder="Precio" data-testid="pricing-price" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
        <GoldButton onClick={save} disabled={!form.package_name_es} data-testid="pricing-save-btn">
          {t("btn.save")}
        </GoldButton>
      </div>

      <ul className="mt-8 space-y-2 text-sm" data-testid="pricing-list">
        {items.map((p) => (
          <li key={p.id} className="flex items-center justify-between border-b border-[#D8D3CB] py-2">
            <span>
              {p.package_name_es} · {p.product_type} · {p.kind}
            </span>
            <span className="flex items-center gap-4">
              <span className="font-serif-display text-[#8F3329]">${Number(p.price).toFixed(2)}</span>
              <button
                type="button"
                data-testid={`pricing-delete-${p.id}`}
                onClick={async () => {
                  await api.delete(`/admin/pricing/${p.id}`);
                  load();
                }}
                className="text-xs underline"
              >
                ✕
              </button>
            </span>
          </li>
        ))}
      </ul>
      <AdminPrintPrices />
    </div>
  );
}
